import { useEffect, useMemo, useState } from 'react';
import { Copy, Crosshair, ExternalLink, Film, ImageOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  formatByteSize,
  overlayClipDetails,
} from '@/lib/videoEditorOverlayDetails';
import { isCardOverlayClip } from '@/lib/cardCarousel';
import {
  isArtifactStickerClip,
  isLayoutBrollClip,
  normalizeHostOverlayOpacity,
} from '@/lib/videoEditorHostOverlays';
import {
  BROLL_VISUAL_STYLES,
  brollVisualStyleMeta,
  sanitizeBrollVisualStyle,
} from '@/lib/reelsShortsBroll';
import {
  ARTIFACT_MOTION_STYLES,
  sanitizeArtifactMotionStyle,
} from '@/lib/artifactStickerMotion';
import { ArtifactSticker } from '@/components/reelsShorts/ArtifactSticker';

import { TwinneeLogo } from '@/components/TwinneeLogo';
async function copyText(value, what) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${what} copied`);
  } catch {
    toast.error('Clipboard blocked by the browser');
  }
}

/** HEAD the asset for its weight — presigned URLs often block it, so failures stay silent. */
function useOutputFileSize(url) {
  const [bytes, setBytes] = useState(null);

  useEffect(() => {
    setBytes(null);
    if (!url) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(url, { method: 'HEAD' });
        const length = Number(res.headers.get('content-length'));
        if (!cancelled && Number.isFinite(length) && length > 0) setBytes(length);
      } catch {
        /* size stays unknown */
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  return bytes;
}

function DetailRow({ label, value, copyable }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="shrink-0 text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
        {label}
      </span>
      <span className="flex min-w-0 items-center gap-1 text-right">
        <span className="truncate text-[11px]" style={{ color: 'var(--text-primary)' }} title={value}>
          {value}
        </span>
        {copyable ? (
          <button
            type="button"
            className="shrink-0 rounded p-0.5 opacity-60 hover:opacity-100 hover:bg-white/10"
            title={`Copy ${label}`}
            aria-label={`Copy ${label}`}
            onClick={() => copyText(value, label)}
          >
            <Copy className="h-3 w-3" />
          </button>
        ) : null}
      </span>
    </div>
  );
}

function TextBlock({ label, value, onCopy, testId }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
          {label}
        </span>
        {onCopy ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px] opacity-70 hover:opacity-100 hover:bg-white/10"
            style={{ color: 'var(--text-secondary)' }}
            onClick={onCopy}
          >
            <Copy className="h-3 w-3" /> Copy
          </button>
        ) : null}
      </div>
      <p
        className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded border px-2 py-1.5 text-[11px] leading-relaxed"
        style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
        data-testid={testId}
      >
        {value}
      </p>
    </div>
  );
}

function FieldHeader({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
        {label}
      </span>
      <div className="flex items-center gap-1">{children}</div>
    </div>
  );
}

function SaveButton({ onClick, testId }) {
  return (
    <button
      type="button"
      className="rounded border px-1.5 py-0.5 text-[10px]"
      style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
      onClick={onClick}
      data-testid={testId}
    >
      Save
    </button>
  );
}

/**
 * Details popup for a timeline overlay clip — opened by double-clicking the clip.
 * The on-screen text and generation prompt are editable and the artifact can be
 * generated / re-generated here.
 */
export function OverlayClipDetailsDialog({
  clip,
  aspectRatio = '9:16',
  open,
  onOpenChange,
  onPromptChange,
  onDisplayTextChange,
  onAnimationStyleChange,
  onVisualStyleChange,
  onHasBackgroundChange,
  onOpacityChange,
  defaultVisualStyle = '',
  onGenerate,
  onLocateInPreview,
}) {
  const details = useMemo(
    () => (clip ? overlayClipDetails(clip, { aspectRatio }) : null),
    [clip, aspectRatio],
  );
  const clipId = clip?.id || '';
  const savedPrompt = details?.prompt || '';
  const savedDisplayText = details?.displayText || '';
  const [promptDraft, setPromptDraft] = useState(savedPrompt);
  const [displayDraft, setDisplayDraft] = useState(savedDisplayText);
  const [generatingMedia, setGeneratingMedia] = useState('');
  const [natural, setNatural] = useState(null);
  const [imageFailed, setImageFailed] = useState(false);
  const outputUrl = details?.outputUrl || '';
  const fileBytes = useOutputFileSize(outputUrl);

  useEffect(() => {
    setPromptDraft(savedPrompt);
  }, [clipId, savedPrompt]);

  useEffect(() => {
    setDisplayDraft(savedDisplayText);
  }, [clipId, savedDisplayText]);

  useEffect(() => {
    setNatural(null);
    setImageFailed(false);
  }, [outputUrl]);

  if (!details) return null;

  const promptDirty = promptDraft.trim() !== savedPrompt.trim();
  const canGenerate = Boolean(onGenerate) && Boolean(promptDraft.trim());
  const generating = Boolean(generatingMedia);
  const hasOutput = Boolean(outputUrl);
  const isVideoOutput = clip?.generatedMedia === 'video';
  const isCard = isCardOverlayClip(clip);
  const isLayout = isLayoutBrollClip(clip);
  const isArtifact = isArtifactStickerClip(clip) && !isCard;
  const motionStyle = sanitizeArtifactMotionStyle(clip?.animationStyle);
  const visualStyle = sanitizeBrollVisualStyle(clip?.visualStyle || defaultVisualStyle);
  const hasBackground = clip?.hasBackground != null ? Boolean(clip.hasBackground) : isLayout;
  const showGenerateLooks = Boolean(onGenerate);
  const opacity = normalizeHostOverlayOpacity(clip?.opacity);
  const opacityPct = Math.round(opacity * 100);

  const displayDirty = displayDraft.trim() !== savedDisplayText.trim();

  const savePrompt = () => {
    if (!promptDirty) return;
    onPromptChange?.(promptDraft.trim());
    toast.success('Prompt saved');
  };

  const saveDisplayText = () => {
    if (!displayDirty) return;
    onDisplayTextChange?.(displayDraft.trim());
    toast.success('On-screen text saved');
  };

  const runGenerate = async (media) => {
    if (!canGenerate || generating) return;
    setGeneratingMedia(media);
    try {
      await onGenerate({
        media,
        prompt: promptDraft.trim(),
        visualStyle,
        hasBackground,
      });
    } finally {
      setGeneratingMedia('');
    }
  };

  const sourceSizeLabel = [
    natural ? `${natural.width} × ${natural.height} px` : '',
    formatByteSize(fileBytes),
  ].filter(Boolean).join(' · ');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[88vh] overflow-y-auto"
        style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)' }}
        data-testid="overlay-clip-details-dialog"
      >
        <DialogHeader>
          <DialogTitle className="truncate" style={{ color: 'var(--text-primary)' }}>
            {details.title}
          </DialogTitle>
          <DialogDescription style={{ color: 'var(--text-tertiary)' }}>
            {details.laneLabel} · {details.kindLabel}
          </DialogDescription>
        </DialogHeader>

        {onLocateInPreview ? (
          <button
            type="button"
            className="self-start rounded border px-2 py-1 text-[11px] flex items-center gap-1 hover:bg-white/10"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
            onClick={() => {
              onLocateInPreview();
              onOpenChange?.(false);
            }}
            data-testid="overlay-details-locate-in-preview"
          >
            <Crosshair className="h-3 w-3" />
            Show where it sits on the preview
          </button>
        ) : null}

        {!isCard && onOpacityChange ? (
          <label
            className="flex flex-col gap-1.5 rounded border px-3 py-2.5"
            style={{
              borderColor: 'rgba(34, 211, 238, 0.35)',
              background: 'var(--bg-secondary)',
            }}
            data-testid="overlay-details-opacity"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wide text-cyan-300/90">
                Opacity
              </span>
              <span className="text-[11px] font-semibold tabular-nums text-cyan-200">
                {opacityPct}%
              </span>
            </div>
            <Slider
              min={5}
              max={100}
              step={1}
              value={[opacityPct]}
              onValueChange={([pct]) => {
                onOpacityChange(normalizeHostOverlayOpacity((Number(pct) || 100) / 100));
              }}
              className={[
                'w-full',
                '[&>span]:bg-cyan-400/25',
                '[&>span>span]:bg-cyan-400',
                '[&_[role=slider]]:border-cyan-300/70',
                '[&_[role=slider]]:bg-cyan-50',
              ].join(' ')}
              data-testid="overlay-details-opacity-slider"
            />
            <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
              Same control as the cyan bar on the selected overlay in preview. Burned into export.
            </p>
          </label>
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
              Output
            </span>
            <div
              className="flex min-h-[160px] items-center justify-center overflow-visible rounded border px-3 py-4"
              style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
            >
              {generating ? (
                <span className="flex flex-col items-center gap-1 px-3 py-6 text-center text-[11px]"
                  style={{ color: 'var(--text-tertiary)' }}>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating {generatingMedia}…
                </span>
              ) : hasOutput && isArtifact ? (
                <div className="w-full max-h-60" data-testid="overlay-clip-details-output">
                  <ArtifactSticker
                    text={details.displayText || details.title}
                    imageUrl={outputUrl}
                    mediaKind={isVideoOutput ? 'video' : 'image'}
                    motionStyle={motionStyle}
                    onMediaSize={(size) => {
                      if (size) {
                        setNatural(size);
                        setImageFailed(false);
                      } else {
                        setImageFailed(true);
                      }
                    }}
                  />
                </div>
              ) : hasOutput && isVideoOutput ? (
                <video
                  src={outputUrl}
                  className="max-h-60 w-full object-contain"
                  controls
                  loop
                  muted
                  playsInline
                  onLoadedMetadata={(event) => setNatural({
                    width: event.currentTarget.videoWidth,
                    height: event.currentTarget.videoHeight,
                  })}
                  data-testid="overlay-clip-details-output-video"
                />
              ) : hasOutput && !imageFailed ? (
                <img
                  src={outputUrl}
                  alt={details.title}
                  className="max-h-60 w-full object-contain"
                  onLoad={(event) => setNatural({
                    width: event.currentTarget.naturalWidth,
                    height: event.currentTarget.naturalHeight,
                  })}
                  onError={() => setImageFailed(true)}
                  data-testid="overlay-clip-details-output"
                />
              ) : (
                <span className="flex flex-col items-center gap-1 px-3 py-6 text-center text-[11px]"
                  style={{ color: 'var(--text-tertiary)' }}>
                  <ImageOff className="h-4 w-4" />
                  {outputUrl ? 'Preview could not be loaded' : 'No image generated yet'}
                </span>
              )}
            </div>
            {sourceSizeLabel ? (
              <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }} data-testid="overlay-clip-details-source-size">
                Source: {sourceSizeLabel}
              </p>
            ) : null}
            {outputUrl ? (
              <div className="flex items-center gap-2">
                <a
                  href={outputUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px]"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
                >
                  <ExternalLink className="h-3 w-3" /> Open original
                </a>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px]"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
                  onClick={() => copyText(outputUrl, 'Output URL')}
                >
                  <Copy className="h-3 w-3" /> Copy URL
                </button>
              </div>
            ) : null}
            {details.outputFileName ? (
              <p className="truncate text-[10px]" style={{ color: 'var(--text-tertiary)' }} title={details.outputFileName}>
                {details.outputFileName}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <FieldHeader label="Generation prompt">
                {promptDraft ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px] opacity-70 hover:opacity-100 hover:bg-white/10"
                    style={{ color: 'var(--text-secondary)' }}
                    onClick={() => copyText(promptDraft, 'Prompt')}
                  >
                    <Copy className="h-3 w-3" /> Copy
                  </button>
                ) : null}
                {onPromptChange && promptDirty ? (
                  <SaveButton onClick={savePrompt} testId="overlay-clip-details-save-prompt" />
                ) : null}
              </FieldHeader>
              <Textarea
                value={promptDraft}
                onChange={(event) => setPromptDraft(event.target.value.slice(0, 800))}
                onBlur={savePrompt}
                rows={4}
                placeholder="Describe the artifact to generate"
                className="min-h-[88px] text-[11px]"
                style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                disabled={!onPromptChange}
                data-testid="overlay-clip-details-prompt"
              />
            </div>

            {onGenerate ? (
              <div className="flex flex-col gap-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] disabled:opacity-40"
                    style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
                    disabled={!canGenerate || generating}
                    onClick={() => runGenerate('image')}
                    data-testid="overlay-clip-generate-image"
                  >
                    {generatingMedia === 'image'
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <TwinneeLogo className="h-3 w-3" />}
                    {hasOutput ? 'Regenerate image' : 'Generate image'}
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] disabled:opacity-40"
                    style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
                    disabled={!canGenerate || generating}
                    onClick={() => runGenerate('video')}
                    data-testid="overlay-clip-generate-video"
                  >
                    {generatingMedia === 'video'
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Film className="h-3 w-3" />}
                    {hasOutput && isVideoOutput ? 'Regenerate video' : 'Generate video'}
                  </button>
                </div>
                <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                  {canGenerate
                    ? 'Generating saves the prompt, look and framing, and reuses this exact asset in the final render.'
                    : 'Add a prompt to enable generation.'}
                </p>
              </div>
            ) : null}

            {showGenerateLooks ? (
              <div className="flex flex-col gap-2" data-testid="overlay-clip-details-look">
                <div className="flex flex-col gap-1">
                  <FieldHeader label="Visual style" />
                  <div className="flex max-h-28 flex-wrap gap-1 overflow-y-auto">
                    {BROLL_VISUAL_STYLES.map((row) => {
                      const selected = visualStyle === row.value;
                      return (
                        <button
                          key={row.value}
                          type="button"
                          title={row.directive}
                          aria-pressed={selected}
                          disabled={!onVisualStyleChange}
                          onClick={() => onVisualStyleChange?.(row.value)}
                          className="rounded border px-2 py-1 text-[11px]"
                          style={{
                            borderColor: selected ? 'var(--primary)' : 'var(--glass-border)',
                            color: selected ? 'var(--primary)' : 'var(--text-secondary)',
                            background: selected ? 'color-mix(in srgb, var(--primary) 12%, transparent)' : 'transparent',
                          }}
                          data-testid={`overlay-clip-visual-style-${row.value}`}
                        >
                          {row.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                    {brollVisualStyleMeta(visualStyle)?.directive}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <FieldHeader label="Framing" />
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      aria-pressed={!hasBackground}
                      disabled={!onHasBackgroundChange}
                      onClick={() => onHasBackgroundChange?.(false)}
                      className="rounded border px-2 py-1 text-[11px]"
                      style={{
                        borderColor: !hasBackground ? 'var(--primary)' : 'var(--glass-border)',
                        color: !hasBackground ? 'var(--primary)' : 'var(--text-secondary)',
                        background: !hasBackground ? 'color-mix(in srgb, var(--primary) 12%, transparent)' : 'transparent',
                      }}
                      data-testid="overlay-clip-framing-cutout"
                    >
                      Cutout
                    </button>
                    <button
                      type="button"
                      aria-pressed={hasBackground}
                      disabled={!onHasBackgroundChange}
                      onClick={() => onHasBackgroundChange?.(true)}
                      className="rounded border px-2 py-1 text-[11px]"
                      style={{
                        borderColor: hasBackground ? 'var(--primary)' : 'var(--glass-border)',
                        color: hasBackground ? 'var(--primary)' : 'var(--text-secondary)',
                        background: hasBackground ? 'color-mix(in srgb, var(--primary) 12%, transparent)' : 'transparent',
                      }}
                      data-testid="overlay-clip-framing-background"
                    >
                      Whole image
                    </button>
                  </div>
                  <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                    {hasBackground
                      ? 'Full scene with an environment behind the subject.'
                      : 'Isolated subject on a transparent background.'}
                  </p>
                </div>
              </div>
            ) : null}

            {onDisplayTextChange ? (
              <div className="flex flex-col gap-1">
                <FieldHeader label="On-screen text">
                  {displayDirty ? (
                    <SaveButton
                      onClick={saveDisplayText}
                      testId="overlay-clip-details-save-display-text"
                    />
                  ) : null}
                </FieldHeader>
                <Input
                  value={displayDraft}
                  onChange={(event) => setDisplayDraft(event.target.value.slice(0, 160))}
                  onBlur={saveDisplayText}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      event.currentTarget.blur();
                    }
                  }}
                  placeholder={isCard ? 'Card title drawn on screen' : isLayout ? 'Optional pane label' : 'Sticker copy drawn on screen'}
                  className="h-8 text-[11px]"
                  style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                  data-testid="overlay-clip-details-display-text-input"
                />
                <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                  {isCard
                    ? 'Drawn on this card in the preview and in the final render.'
                    : isLayout
                      ? 'Optional label for this B-ROLL pane.'
                      : 'Used as the sticker copy for text artifacts.'}
                </p>
              </div>
            ) : (
              <TextBlock label="On-screen text" value={details.displayText} testId="overlay-clip-details-display-text" />
            )}
            <TextBlock label="Spoken trigger" value={details.triggerText} testId="overlay-clip-details-trigger" />
          </div>
        </div>

        {isArtifact && onAnimationStyleChange ? (
          <div className="flex flex-col gap-1.5" data-testid="overlay-clip-details-animation">
            <FieldHeader label="On-screen animation" />
            <div className="flex flex-wrap gap-1">
              {ARTIFACT_MOTION_STYLES.map((row) => {
                const selected = motionStyle === row.id;
                return (
                  <button
                    key={row.id}
                    type="button"
                    title={row.hint}
                    aria-pressed={selected}
                    onClick={() => onAnimationStyleChange(row.id)}
                    className="rounded border px-2 py-1 text-[11px]"
                    style={{
                      borderColor: selected ? 'var(--primary)' : 'var(--glass-border)',
                      color: selected ? 'var(--primary)' : 'var(--text-secondary)',
                      background: selected ? 'color-mix(in srgb, var(--primary) 12%, transparent)' : 'transparent',
                    }}
                    data-testid={`overlay-clip-animation-${row.id}`}
                  >
                    {row.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
              {ARTIFACT_MOTION_STYLES.find((row) => row.id === motionStyle)?.hint}. Plays on the preview and in the final render.
            </p>
          </div>
        ) : null}

        <div
          className="mt-1 divide-y divide-white/5 rounded border px-2 py-1"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
        >
          {details.fields.map((field) => (
            <DetailRow key={field.key} label={field.label} value={field.value} copyable={field.copyable} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default OverlayClipDetailsDialog;
